export const IPOH_AWNING_MATERIALS = [
    { code: "A", name: "ACP" },
    { code: "P", name: "POLY" },
    { code: "PM", name: "PU METAL" },
    { code: "PF", name: "PU FOAM" },
    { code: "MD", name: "METAL DECK" },
    { code: "TMD", name: "TRANSPARENT METAL DECK" },
    { code: "FG", name: "FIBER GLASS" },
    { code: "G", name: "GLASS" },
    { code: "AF", name: "ALUMINIUM FLUTED PANEL" },
    { code: "AFF", name: "ALUMINIUM FLUTED PANEL WITH FOAM" },
];

export const IPOH_MATERIAL_COLOR_FIELDS = {
    A: "acpColor",
    P: "polycarbonateColor",
    PM: "puMetalColor",
    PF: "puFoamColor",
    MD: "metaldeckColor",
    FG: "fiberglassColor",
    G: "glassColor",
    AF: "aluminiumFlutedPanelColor",
    AFF: "aluminiumFlutedPanelFoamColor",
};

export const clearUnselectedIpohMaterialColors = (items = [], materials = []) => {
    const selected = new Set(materials);
    return items.map((item) => {
        const next = { ...item };
        Object.entries(IPOH_MATERIAL_COLOR_FIELDS).forEach(([code, field]) => {
            if (!selected.has(code)) next[field] = "";
        });
        return next;
    });
};

const packageName = (label) =>
    label
        .replace(/[’′]/g, "'")
        .replace(/(\d)\s*["']+\s*X\s*(\d)\s*["']+/gi, "$1''X$2''");

const packageOption = (code, label) => ({
    code,
    label,
    name: packageName(label),
});

export const IPOH_AWNING_PACKAGES = [
    packageOption("01", "MAIN SUPPORT + PILLAR"),
    packageOption("02", "ROCKET"),
    packageOption("03", "SINGLE HOLLOW"),
    packageOption("04", "DOUBLE HOLLOW"),
    packageOption("05", '2" X 6" FRAME'),
    packageOption("06", '2" X 4" FRAME (GLASS)'),
    packageOption("07", "T-BAR (GLASS)"),
    packageOption("08", "GI TRIANGLE DESIGN"),
    packageOption("09", "GI TRIANGLE + SINGLE T-LIGHT"),
    packageOption("10", "GI DESIGN + INSIDE GUTTER"),
    packageOption("11", "GI DESIGN (\u5de5) + INSIDE GUTTER"),
    packageOption("12", "2\u2019\u2019 X 6\u2019\u2019 SINGLE T-LIGHT"),
    packageOption("13", "DOUBLE T-LIGHT"),
    packageOption("14", "TETTRO"),
    packageOption("15", '2" X 4" FRAME'),
    packageOption("16", "ALUMINIUM G PROFILE (IN)"),
    packageOption("17", "ALUMINIUM G PROFILE (OUT)"),
    packageOption("18", "2\u2019\u2019 X 6\u2019\u2019 C-PURLIN"),
];

export const IPOH_AWNING_CEILINGS = [
    { code: "CB", name: "BAMBOO CEILING" },
    { code: "CS", name: "METAL STRIP CEILING" },
    { code: "CA", name: "ALUMINIUM CEILING" },
    { code: "CF", name: "PU FOAM CEILING" },
    { code: "CT", name: "TIMBERFLEX CEILING" },
];

const MATERIAL_CODES = IPOH_AWNING_MATERIALS.map((item) => item.code).sort(
    (left, right) => right.length - left.length
);
const PACKAGE_CODES = new Set(IPOH_AWNING_PACKAGES.map((item) => item.code));
const CEILING_CODES = new Set(IPOH_AWNING_CEILINGS.map((item) => item.code));

const capitalizeProductName = (value) =>
    String(value || "")
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");

export const EMPTY_IPOH_AWNING_SPEC = {
    materials: [],
    pipe: false,
    package: "",
    packageSize: "",
    ceiling: "",
};

const G_PROFILE_PACKAGES = new Set(["16", "17"]);

export const ipohPackageNeedsSize = (packageCode) => G_PROFILE_PACKAGES.has(packageCode);

const findByCode = (options, code) =>
    options.find((item) => item.code === code);

export const isCompleteIpohAwningSpec = (spec) =>
    Boolean(
        spec?.package &&
            PACKAGE_CODES.has(spec.package) &&
            Array.isArray(spec.materials) &&
            spec.materials.length > 0 &&
            (!ipohPackageNeedsSize(spec.package) ||
                spec.packageSize === "8" ||
                spec.packageSize === "12")
    );

export const ipohAwningSpecMessage = (spec) => {
    if (
        !spec?.package ||
        !PACKAGE_CODES.has(spec.package) ||
        !Array.isArray(spec.materials) ||
        spec.materials.length === 0
    ) {
        return "Please select at least one material and one package";
    }
    if (ipohPackageNeedsSize(spec.package) && spec.packageSize !== "8" && spec.packageSize !== "12") {
        return "Please select 8'' or 12'' for Aluminium G Profile";
    }
    return "";
};

export const buildIpohAwningProduct = (spec = EMPTY_IPOH_AWNING_SPEC) => {
    const materials = IPOH_AWNING_MATERIALS.filter((item) =>
        spec.materials?.includes(item.code)
    );
    const packageOption = findByCode(IPOH_AWNING_PACKAGES, spec.package);
    const ceiling = findByCode(IPOH_AWNING_CEILINGS, spec.ceiling);
    const materialCode =
        materials.length > 1
            ? materials.map((item) => item.code).join("+")
            : materials[0]?.code || "";
    const sizeCode =
        ipohPackageNeedsSize(spec.package) &&
        (spec.packageSize === "8" || spec.packageSize === "12")
            ? `(${spec.packageSize})`
            : "";
    const code = `${materialCode}${packageOption?.code || ""}${sizeCode}${
        spec.pipe ? "(P)" : ""
    }${ceiling ? `+${ceiling.code}` : ""}`;

    const sizedPackageName =
        packageOption?.name && sizeCode
            ? `${packageOption.name} ${spec.packageSize}''`
            : packageOption?.name || "";
    const description = [materials.map((item) => item.name).join(" & "), sizedPackageName]
        .filter(Boolean)
        .join(" & ");
    const name = capitalizeProductName(
        [
            description,
            spec.pipe ? "\u540aPIPE DESIGN" : "",
            ceiling?.name || "",
        ]
            .filter(Boolean)
            .join(" + ")
    );

    return { code, name };
};

const tokenizeMaterialCodes = (token) => {
    const found = [];
    let index = 0;
    while (index < token.length) {
        const match = MATERIAL_CODES.find((code) => token.startsWith(code, index));
        if (!match) return null;
        found.push(match);
        index += match.length;
    }
    return found;
};

export const parseIpohAwningCode = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return null;

    let rest = raw;
    let ceiling = "";
    const ceilingMatch = rest.match(/\+([A-Z]+)$/);
    if (ceilingMatch && CEILING_CODES.has(ceilingMatch[1])) {
        ceiling = ceilingMatch[1];
        rest = rest.slice(0, -ceilingMatch[0].length);
    }

    let pipe = false;
    if (rest.endsWith("(P)")) {
        pipe = true;
        rest = rest.slice(0, -3);
    }

    let packageSize = "";
    const sizeMatch = rest.match(/\((8|12)\)$/);
    if (sizeMatch) {
        packageSize = sizeMatch[1];
        rest = rest.slice(0, -sizeMatch[0].length);
    }

    const packageMatch = rest.match(/(\d{2})$/);
    if (!packageMatch || !PACKAGE_CODES.has(packageMatch[1])) return null;

    const materialPart = rest.slice(0, -2);
    if (!materialPart) return null;

    const materials = [];
    for (const token of materialPart.split("+")) {
        const parsed = tokenizeMaterialCodes(token);
        if (!parsed?.length) return null;
        materials.push(...parsed);
    }

    return {
        materials,
        pipe,
        package: packageMatch[1],
        packageSize,
        ceiling,
    };
};

export const ipohAwningFieldsFromCode = (code, title) => {
    const spec = parseIpohAwningCode(code);
    if (!spec) return null;
    const built = buildIpohAwningProduct(spec);
    return {
        ipohAwningSpec: spec,
        ipohProductCode: built.code,
        ipohProductName: String(title || "").trim() || built.name,
    };
};

export const resolveIpohAwningOrderFields = (color) => {
    const code = String(color?.ipohProductCode || "").trim();
    if (!code) return null;
    const name = String(color?.ipohProductName || "").trim();
    const specItem = color?.ipohSpecItem || {};
    const hasSpec = Object.entries(specItem).some(([key, value]) => {
        if (key === "type") return false;
        return value !== undefined && value !== null && value !== "" && value !== false;
    });
    const savedItems = Array.isArray(color?.items)
        ? color.items.filter((item) => item && typeof item === "object")
        : [];
    return {
        code,
        title: name || undefined,
        items: savedItems.length
            ? savedItems
            : hasSpec
              ? [{ ...specItem, type: "others" }]
              : [],
        itemCode: undefined,
        type: undefined,
        typeDescription: undefined,
    };
};
